import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';

import { V2GuaranteeCreatorType, VisitView } from '@lib/guarantees/data-access';
import { selectBreachRows } from '@lib/guarantees/data-access/store/guarantees-v2.selectors';
import { ChangeHistoryComponent } from '@lib/guarantees/feature/components/common/change-history.component';
import { ChipComponent } from '@lib/guarantees/feature/components/common/chip.component';
import { DiagnosisChipComponent } from '@lib/guarantees/feature/components/common/diagnosis-chip.component';
import { displayName } from '@lib/guarantees/feature/components/common/display-name';
import { FactComponent } from '@lib/guarantees/feature/components/common/fact.component';
import {
  CREATOR_HINT_KEY,
  STATUS_HINT_KEY,
  STATUS_LABEL_KEY,
  STATUS_TONE,
  visitCategoryIcon,
  visitCategoryLabelKey,
} from '@lib/guarantees/feature/components/common/guarantee-vocabulary';
import { PanelSectionComponent } from '@lib/guarantees/feature/components/common/panel-section.component';
import { PrescriptionStatsComponent } from '@lib/guarantees/feature/components/common/prescription-stats.component';
import { RecordBreachView, RecordBreachesComponent } from '@lib/guarantees/feature/components/common/record-breaches.component';
import { RecordedNoteComponent } from '@lib/guarantees/feature/components/common/recorded-note.component';
import { RecordedNote, recordedNotesOf } from '@lib/guarantees/feature/components/common/recorded-notes';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';

import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';

import { CalloutComponent } from '../common/callout.component';

/** A note gathered from one of the visit's lines, which is why it has to name which. */
interface VisitNote {
  note: RecordedNote;
  prescriptionName: string;
}

/**
 * Everything the card does not state: who raised it, the contract, the visit, the notes on its
 * rulings. Deliberately disjoint from the card — a fact in both is maintained twice.
 */
@Component({
  selector: 'guarantees-guarantee-details',
  standalone: true,
  imports: [
    DatePipe,
    MatIcon,
    MatTooltip,
    TranslatePipe,
    RecordBreachesComponent,
    ChangeHistoryComponent,
    ChipComponent,
    DiagnosisChipComponent,
    FactComponent,
    PanelSectionComponent,
    PrescriptionStatsComponent,
    RecordedNoteComponent,
    CalloutComponent,
  ],
  templateUrl: './guarantee-details.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GuaranteeDetailsComponent {
  visit = input.required<VisitView>();

  /** Following the chain from here — the page owns where that navigation goes. */
  guaranteeOpened = output<string>();

  private readonly store = inject(Store);
  private readonly breachRows = this.store.selectSignal(selectBreachRows);

  protected readonly creatorTypes = V2GuaranteeCreatorType;

  protected readonly statusTone = computed(() => STATUS_TONE[this.visit().status]);
  protected readonly statusLabelKey = computed(() => STATUS_LABEL_KEY[this.visit().status]);
  protected readonly statusHintKey = computed(() => STATUS_HINT_KEY[this.visit().status]);

  /** Read once: three blocks of the visit section turn on whether there was an appointment at all. */
  protected readonly appointmentId = computed(() => this.visit().appointmentId);

  /** Zero is left unsaid — a visit shared with nothing is the ordinary case, not a fact to report. */
  protected readonly sharedVisitCount = computed(() => this.visit().sharedVisitGuaranteeIds.length);

  protected readonly categoryIcon = computed(() => visitCategoryIcon(this.visit().visitCategory));
  protected readonly categoryLabelKey = computed(() => visitCategoryLabelKey(this.visit().visitCategory));

  protected readonly creatorHintKey = computed(() => CREATOR_HINT_KEY[this.visit().creatorType] ?? CREATOR_HINT_KEY.Unknown);

  protected readonly createdByName = computed(() => displayName(this.visit().createdByName));
  protected readonly updatedByName = computed(() => displayName(this.visit().updatedByName));
  protected readonly deletedByName = computed(() => displayName(this.visit().deletedByName));

  /** The analyser's findings against this record and its lines, out of the window-wide list. */
  protected readonly breaches = computed<RecordBreachView[]>(() =>
    this.breachRows()
      .filter(row => row.guaranteeId === this.visit().guaranteeId)
      .map(row => ({ key: row.key, code: row.code, detectedAt: row.detectedAt, severity: row.severity }))
  );

  /** Every diagnosis the guarantee states, including those nothing is prescribed against. */
  protected readonly diagnoses = computed(() => this.visit().diagnoses);

  /**
   * Every note recorded against one of its lines. They live per line and are never seen together
   * anywhere else, which is what this section is for.
   */
  protected readonly notes = computed<VisitNote[]>(() =>
    this.visit()
      .diagnosisGroups.flatMap(group => group.lines)
      .flatMap(line =>
        recordedNotesOf(line.prescription).map(note => ({
          note,
          prescriptionName: line.prescription.name || line.prescription.code || '',
        }))
      )
  );

  /** Only reachable while the link has a target, which is what the control's disabled state says. */
  protected openPrecedingGuarantee(): void {
    const guaranteeId = this.visit().precededByGuaranteeId;
    if (guaranteeId) {
      this.guaranteeOpened.emit(guaranteeId);
    }
  }
}
